import { Component, inject, Injectable } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseDialogService, NonNullableConfigData, TilbyDialogContentComponent, TilbyDialogToolbarComponent } from '@tilby/tilby-ui-lib/components/tilby-dialog';
import { CustomFormArray, CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { lastValueFrom } from 'rxjs';
import { ItemsBarcodeQty } from 'src/app/features/items/components/items-module/items-tab/item-details/item-details-tabs/item-general/item-general.model';
import { EditBarcodeForm, EditBarcodeFormGroup, EditBarcodeListForm, EditBarcodeListFormGroup } from './edit-barcode.model';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BarcodeManagerService, EnvironmentInfoService } from 'src/app/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertDialogService } from '../alert-dialog';

type EditBarcodeData = {
  title: string,
  barcodes: Array<ItemsBarcodeQty>
}

@Component({
  selector: 'app-edit-barcode-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
    TranslateModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatTooltipModule
  ],
  templateUrl: './edit-barcode-dialog.component.html',
  styleUrl: './edit-barcode-dialog.component.scss'
})
export class EditBarcodeDialogComponent {

  private readonly matDialogRef = inject(MatDialogRef);
  private readonly environmentInfoService = inject(EnvironmentInfoService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translate = inject(TranslateService);
  private readonly barcodeManagerService = inject(BarcodeManagerService);
  protected readonly data: EditBarcodeData = inject(MAT_DIALOG_DATA);

  protected isCameraBarcodeSupported = this.environmentInfoService.isCameraBarcodeSupported();

  protected form!: EditBarcodeListFormGroup;
  protected formReady = false;

  constructor() {
    this.buildForm();
    this.setExistingBarcodes(this.data.barcodes);
    this.formReady = true;
  }

  private buildForm() {
    this.form = new CustomFormGroup<EditBarcodeListForm>({
      barcodeList: new CustomFormArray<EditBarcodeFormGroup>(
        []
      )
    })
  }

  protected get barcodeList(): CustomFormArray<EditBarcodeFormGroup> {
    return this.form.controls.barcodeList as CustomFormArray<EditBarcodeFormGroup>;
  }

  protected getBarcodeValue(index: number) {
    return this.barcodeList?.at(index).controls.barcode;
  }

  protected getQtyBarcode(index: number) {
    return this.barcodeList?.at(index).controls.qty;
  }

  private setExistingBarcodes(barcodes: ItemsBarcodeQty[]) {
    barcodes?.forEach((b: ItemsBarcodeQty) => {
      this.barcodeList.push(this.createBarcodeGroup(b))
    })
  }
  
  private createBarcodeGroup(barcode?: ItemsBarcodeQty): CustomFormGroup<EditBarcodeForm> {
    const barcodeVal = barcode?.barcode || null;
    const qtyVal = barcode?.qty || null;

    const specialBarcodeRegex = /^.{6,7}[QPC]$/i;
    const shouldDisableQty = (val: string | null) => val ? specialBarcodeRegex.test(val) : false;

    const group = new CustomFormGroup<EditBarcodeForm>({
      id: new CustomFormControl(barcode?.id || null),
      item_id: new CustomFormControl((barcode as any)?.item_id || null),
      combination_id: new CustomFormControl((barcode as any)?.combination_id || null),
      barcode: new CustomFormControl(
        barcodeVal,
        { validators: [Validators.required] }
      ),
      qty: new CustomFormControl(
        { value: qtyVal, disabled: shouldDisableQty(barcodeVal)  },
        { validators: Validators.min(1) }
      )
    })

    group.get('barcode')?.valueChanges.subscribe((value: string) => {
      const qtyControl = group.get('qty');
      
      if (shouldDisableQty(value)) {
        qtyControl?.disable({ emitEvent: false });
        qtyControl?.setValue(null, { emitEvent: false }); 
      } else {
        qtyControl?.enable({ emitEvent: false });
      }
    });

    return group;
  }

  protected addBarcode() {
    this.barcodeList.insert(0, this.createBarcodeGroup())
  }

  protected removeBarcode(index: number) {
    this.barcodeList.removeAt(index);
  }

  protected async openBarcodeAndSearch(index: number) {
    const scannedValue = await this.barcodeManagerService.openCameraBarcode();
    
    if (scannedValue) {
      const isDuplicate = this.barcodeList.getRawValue().some((item: any, idx: number) => 
        item.barcode === scannedValue && idx !== index
      );

      if (isDuplicate) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translate.instant('ITEMS.DETAILS.BARCODE_ALREADY_PRESENT')
          }
        });
        return;
      }
      this.barcodeList.at(index).get('barcode')?.setValue(scannedValue);
    }
  }

  protected confirm() {
    this.matDialogRef.close(this.barcodeList.getRawValue());
  }
}

@Injectable({
  providedIn: "root",
})
export class EditBarcodeDialogService extends BaseDialogService {
  private readonly dialogRef = inject(MatDialog);

  public openDialog(config?: NonNullableConfigData<EditBarcodeData>) {
    const data: EditBarcodeData = config?.data || { title: "Barcode", barcodes: []};
    
    const configEdited: NonNullableConfigData<EditBarcodeData> = {
      ...config,
      ...this.switchMobileDesktopDimensions({ width: "80vw", maxWidth: "850px" }, {fullScreenForMobile: true}),
      disableClose: true,
      data
    };

    return lastValueFrom(this.dialogRef.open(EditBarcodeDialogComponent, configEdited).afterClosed());
  }
}
